import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  Box,
  Container,
  Tabs,
  Tab,
  Skeleton,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  IconButton, // Import IconButton
  Menu, // Import Menu
  MenuItem, // Import MenuItem
  ListItemIcon, // Import ListItemIcon
  ListItemText, // Import ListItemText
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Card,
  CardHeader,
  CardContent,
  Divider,
  Stack
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'; // Import AddCircleOutlineIcon
import MoreVertIcon from '@mui/icons-material/MoreVert'; // Import MoreVertIcon
import EditIcon from '@mui/icons-material/Edit'; // Import EditIcon
import DeleteIcon from '@mui/icons-material/Delete'; // Import DeleteIcon
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import {
  Star as StarIcon,
  Group as GroupIcon,
  PersonAdd as PersonAddIcon,
  Business as BusinessIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { Role, Team } from '../types'; // Import Team type
import { PERMISSIONS } from '../utils/permissions';

// Hooks
import useOrganizationOverview from '../hooks/useOrganizationOverview';
import useOrganizationMembers from '../hooks/useOrganizationMembers';
import { useConfirmation } from '../hooks/useConfirmation';
import { useOrganization } from '../context/OrganizationContext';
import { useRBAC } from '../context/RBACContext';
import { usePermissions, usePermissionVisibility } from '../hooks/usePermissions';

// Components
import OrganizationHeader from '../components/organization/OrganizationHeader';
import MembersList from '../components/organization/MembersList';
import CreateTeamDialog from '../components/organization/CreateTeamDialog';
import BulkInviteDialog from '../components/organization/BulkInviteDialog';
import ConfirmDialog from '../components/common/dialogs/ConfirmDialog';
import ErrorDisplay from '../components/common/feedback/ErrorDisplay';
import SubscriptionManagement from '../components/subscription/SubscriptionManagement';
import SubscriptionExpirationWarnings from '../components/subscription/SubscriptionExpirationWarnings';
import RoleManager from '../components/rbac/RoleManager';
import OrganizationInvitations from '../components/organization/OrganizationInvitations';
import OrganizationSettings from '../components/organization/OrganizationSettings';
import { userService, organizationService, teamService, subscriptionService, paymentService, invitationService, roleService, contentService, tagService, searchService, authService } from '../services';
import { logError, getErrorMessageString } from '../utils/error-handler';
import { formatDate } from '../utils/date-formatter'; // Import formatDate
import { isLastAdminError } from '../utils/admin-validation-error-handler';
import organizationApi from '../services/api/organization.api';

// Tab panel component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`org-tabpanel-${index}`}
      aria-labelledby={`org-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

// Section error component
interface SectionErrorProps {
  error: any;
  section: string;
  onRetry?: () => void;
}

const SectionError: React.FC<SectionErrorProps> = ({ error, section, onRetry }) => {
  return (
    <Alert 
      severity="warning" 
      sx={{ mb: 2 }}
      action={
        onRetry ? (
          <Button color="inherit" size="small" onClick={onRetry}>
            Retry
          </Button>
        ) : undefined
      }
    >
      Failed to load {section}: {error?.message || 'Unknown error'}
    </Alert>
  );
};

// Row Actions Cell Component for managing per-row menu for Teams
function TeamRowActionsCell({
  team,
  onEdit,
  onDelete,
}: {
  team: Team;
  onEdit: (team: Team) => void;
  onDelete: (team: Team) => void;
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // Check permissions for this team
  const { hasPermission } = usePermissions();
  const canUpdate = hasPermission(PERMISSIONS.TEAM_UPDATE);
  const canDelete = hasPermission(PERMISSIONS.TEAM_DELETE);

  // If user has no permissions for either action, don't show the menu button
  if (!canUpdate && !canDelete) {
    return null;
  }

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAction = (action: 'edit' | 'delete') => {
    switch (action) {
      case 'edit':
        onEdit(team);
        break;
      case 'delete':
        onDelete(team);
        break;
    }
    handleClose();
  };

  return (
    <Box>
      <IconButton
        aria-label="actions"
        aria-controls={`team-actions-menu-${team._id}`}
        aria-haspopup="true"
        onClick={handleClick}
        size="small"
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>

      {/* Actions Menu */}
      <Menu
        id={`team-actions-menu-${team._id}`}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {canUpdate && (
          <MenuItem onClick={() => handleAction('edit')}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit Team</ListItemText>
          </MenuItem>
        )}

        {canDelete && (
          <MenuItem
            onClick={() => handleAction('delete')}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Delete Team</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
}


// Main component
const OrganizationDetail: React.FC = () => {
  const { orgId } = useParams<{ orgId: string }>(); // Use orgId from route param
  // console.log('OrganizationDetail: ID from useParams:', orgId, typeof orgId); // Remove previous log
  const navigate = useNavigate();
  const location = useLocation();
  const { isOpen, confirmationState, confirm, handleConfirm, handleCancel } = useConfirmation();
  
  // Get tab from URL query parameter
  const getTabFromURL = (): number => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab) {
      const tabIndex = parseInt(tab, 10);
      if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 6) {
        return tabIndex;
      }
    }
    return 0; // Default to first tab
  };
  
  // State
  const [tabValue, setTabValue] = useState<number>(getTabFromURL());
  const [roles, setRoles] = useState<Role[]>([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  const [rolesError, setRolesError] = useState<string | null>(null);
  const [createTeamDialogOpen, setCreateTeamDialogOpen] = useState(false);
  const [inviteMemberDialogOpen, setInviteMemberDialogOpen] = useState(false);
  const [leaveOrgDialogOpen, setLeaveOrgDialogOpen] = useState(false);
  const [isLeavingOrg, setIsLeavingOrg] = useState(false);
  const [deleteOrgDialogOpen, setDeleteOrgDialogOpen] = useState(false);
  const [deleteOrgError, setDeleteOrgError] = useState<string | null>(null);
  const [isDeletingOrg, setIsDeletingOrg] = useState(false);
  const [notification, setNotification] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  // Data hooks
  const { 
    organization, 
    teams,
    members: overviewMembers,
    userRole,
    permissions,
    subscription,
    isLoading: isLoadingOrg, 
    loading,
    error: orgError,
    errors,
    updateOrganization,
    deleteOrganization,
    refreshTeams,
    refreshMembers,
    refreshSubscription
  } = useOrganizationOverview(orgId); // Use new overview hook

  const { members } = useOrganizationMembers(orgId || '');
  const { createTeam, syncWithCurrentRoute, refreshData, refreshMemberships, getOrganizationSubscriptions } = useOrganization(); // For team creation and route sync
  const { userPermissions, loadUserPermissions } = useRBAC(); // For permission checks (legacy)
  
  // NEW: Use the permission-aware framework
  const permissionHook = usePermissions();
  const visibility = usePermissionVisibility();

  // State for team actions
  const [editTeamDialogOpen, setEditTeamDialogOpen] = useState(false);
  const [deleteTeamDialogOpen, setDeleteTeamDialogOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [editTeamData, setEditTeamData] = useState({ name: '', description: '' });
  const [isEditingTeam, setIsEditingTeam] = useState(false);
  const [editTeamError, setEditTeamError] = useState<string | null>(null);
  const [isDeletingTeam, setIsDeletingTeam] = useState(false);
  const [deleteTeamError, setDeleteTeamError] = useState<string | null>(null);

  // Server-side pagination state for teams
  const [teamsPaginationModel, setTeamsPaginationModel] = useState({
    page: 0, // MUI DataGrid uses 0-indexed pages
    pageSize: 10,
  });
  const [teamsRowCount, setTeamsRowCount] = useState(0);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [paginatedTeams, setPaginatedTeams] = useState<Team[]>([]);

  // NEW: Permission-based checks using the framework
  const canViewInvitations = permissionHook.canViewInvitations();
  const canInviteMembers = permissionHook.canInviteMembers();
  const canManageTeams = permissionHook.canManageTeams();
  const canManageOrganization = permissionHook.canManageOrganization();
  const canManageRoles = permissionHook.canManageRoles();
  const canManageSubscriptions = permissionHook.canManageSubscriptions();
  
  // Specific permission checks for granular features
  const canCreateTeam = permissionHook.hasPermission(PERMISSIONS.TEAM_CREATE);
  const canDeleteOrganization = permissionHook.hasPermission(PERMISSIONS.ORGANIZATION_DELETE);
  const canViewMembers = permissionHook.hasPermission(PERMISSIONS.MEMBER_READ);
  const canViewTeams = permissionHook.hasPermission(PERMISSIONS.TEAM_READ);

  // Debug logging for permissions (development only)
  React.useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔒 Organization Permission Debug:', {
        context: permissionHook.context,
        totalPermissions: permissionHook.permissions.length,
        permissions: permissionHook.permissions.sort(),
        canViewInvitations,
        canInviteMembers,
        canManageTeams,
        canManageOrganization,
        canManageRoles,
        canManageSubscriptions,
        canCreateTeam,
        canDeleteOrganization,
        canViewMembers,
        canViewTeams,
        // Legacy for comparison
        userRole,
        legacyUserPermissions: userPermissions.map(p => `${p.resource}:${p.action}`)
      });
      
      // Debug specific permission checks
      permissionHook.debugPermissionCheck(PERMISSIONS.INVITATION_READ, 'Invitations tab visibility');
      permissionHook.debugPermissionCheck(PERMISSIONS.MEMBER_INVITE, 'Invite members functionality');
      permissionHook.debugPermissionCheck(PERMISSIONS.TEAM_CREATE, 'Create team functionality');
      permissionHook.debugPermissionCheck(PERMISSIONS.ORGANIZATION_DELETE, 'Delete organization functionality');
      permissionHook.debugPermissionCheck(PERMISSIONS.ORGANIZATION_SETTINGS, 'Organization management');
      permissionHook.debugPermissionCheck(PERMISSIONS.ROLE_ASSIGN, 'Role management');
    }
  }, [permissionHook, canViewInvitations, canInviteMembers, canManageTeams, canManageOrganization, canManageRoles, canManageSubscriptions, canCreateTeam, canDeleteOrganization, canViewMembers, canViewTeams, userRole, userPermissions]);

  // Get subscriptions for this organization
  const orgSubscriptions = React.useMemo(() => {
    if (!orgId) return [];
    return getOrganizationSubscriptions(orgId);
  }, [orgId, getOrganizationSubscriptions]);

  // Define columns for Teams DataGrid
  const teamColumns: GridColDef<Team>[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 2,
      minWidth: 200,
    },
    {
      field: 'createdAt',
      headerName: 'Created At',
      width: 150,
      renderCell: (params: GridRenderCellParams<Team>) => {
        return formatDate(params.row.createdAt);
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      type: 'actions',
      width: 80,
      align: 'right',
      renderCell: (params: GridRenderCellParams<Team>) => (
        <TeamRowActionsCell
          team={params.row}
          onEdit={handleEditTeam}
          onDelete={handleDeleteTeam}
        />
      )
    }
  ];

  // Load roles
  useEffect(() => {
    if (orgId) { // Use orgId
      setIsLoadingRoles(true);
      setRolesError(null);
      // Updated API call to use new endpoint
      roleService.getRoles({ organizationId: orgId, level: 'organization' }) 
        .then(rolesData => {
          setRoles(rolesData as any);
          setRolesError(null);
        })
        .catch(error => {
          logError('Error loading roles', error);
          setRolesError(getErrorMessageString(error));
          // Provide fallback roles for member invitations
          setRoles([
            { _id: 'org-admin', name: 'Organization Admin', description: 'Full administrative access', permissions: [], level: 'organization', createdAt: '', updatedAt: '' },
            { _id: 'org-member', name: 'Organization Member', description: 'Standard member access', permissions: [], level: 'organization', createdAt: '', updatedAt: '' },
            { _id: 'team-admin', name: 'Team Admin', description: 'Administrative access to teams', permissions: [], level: 'team', createdAt: '', updatedAt: '' }
          ]);
        })
        .finally(() => {
          setIsLoadingRoles(false);
        });
    }
  }, [orgId]); // Use orgId
  
  // Sync context with current route when orgId changes
  useEffect(() => {
    if (orgId) {
      syncWithCurrentRoute(orgId);
    }
  }, [orgId, syncWithCurrentRoute]);

  // Load user permissions for current organization
  useEffect(() => {
    if (orgId) {
      loadUserPermissions({ organizationId: orgId });
    }
  }, [orgId, loadUserPermissions]);

  // Update URL when tab changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    params.set('tab', tabValue.toString());
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  }, [tabValue, location.pathname, navigate, location.search]);

  // Function to load paginated teams
  const loadPaginatedTeams = React.useCallback(async () => {
    if (!orgId) return;

    setTeamsLoading(true);
    try {
      const response = await organizationService.listOrganizationTeamsWithMetadata(orgId, {
        page: teamsPaginationModel.page + 1, // Convert from 0-indexed to 1-indexed
        limit: teamsPaginationModel.pageSize
      });

      // Ensure data is an array and map to ensure all teams have required _id field
      const teamsData = Array.isArray(response.data) ? response.data : [];
      const validTeams: Team[] = teamsData.map(team => ({
        ...team,
        _id: team._id || team.id || '',
        id: team.id || team._id || '',
        createdAt: team.createdAt instanceof Date ? team.createdAt.toISOString() : (team.createdAt || ''),
        updatedAt: team.updatedAt instanceof Date ? team.updatedAt.toISOString() : (team.updatedAt || ''),
      }));

      setPaginatedTeams(validTeams);
      setTeamsRowCount(response.metadata?.pagination?.totalCount || 0);
    } catch (error) {
      console.error('Failed to load paginated teams:', error);
      // Fallback to empty array on error
      setPaginatedTeams([]);
      setTeamsRowCount(0);
    } finally {
      setTeamsLoading(false);
    }
  }, [orgId, teamsPaginationModel.page, teamsPaginationModel.pageSize]);

  // Load paginated teams when pagination model changes
  useEffect(() => {
    loadPaginatedTeams();
  }, [loadPaginatedTeams]);


  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  // Team handlers
  const handleCreateTeam = async (data: { name: string; description: string; subscriptionId: string }) => {
    if (!orgId) return { success: false, error: { message: 'No organization ID', code: 'INVALID_REQUEST', status: 400 } };
    try {
      const team = await createTeam({ ...data, organizationId: orgId });

      // Refresh data to update subscriptions usage and team list
      await refreshData();

      // Reload paginated teams
      await loadPaginatedTeams();

      // Navigate to the new team's page
      navigate(`/organizations/${orgId}/teams/${team.id || team._id}`);

      return { success: true };
    } catch (error) {
      return { success: false, error: { message: 'Failed to create team', code: 'UNKNOWN_ERROR', status: 500 } };
    }
  };

  const handleEditTeam = (team: Team) => {
    setSelectedTeam(team);
    setEditTeamData({
      name: team.name || '',
      description: team.description || ''
    });
    setEditTeamDialogOpen(true);
    setEditTeamError(null);
  };

  const handleDeleteTeam = (team: Team) => {
    setSelectedTeam(team);
    setDeleteTeamDialogOpen(true);
    setDeleteTeamError(null);
  };

  const handleCloseEditTeam = () => {
    setEditTeamDialogOpen(false);
    setEditTeamError(null);
    setSelectedTeam(null);
  };

  const handleEditTeamChange = (field: 'name' | 'description') => (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditTeamData(prev => ({ ...prev, [field]: e.target.value }));
    if (editTeamError) setEditTeamError(null);
  };

  const handleSubmitEditTeam = async () => {
    if (!editTeamData.name || !editTeamData.name.trim()) {
      setEditTeamError('Team name is required');
      return;
    }

    if (!selectedTeam) {
      setEditTeamError('No team selected');
      return;
    }

    setIsEditingTeam(true);
    setEditTeamError(null);

    try {
      await teamService.updateTeam(selectedTeam._id, {
        name: editTeamData.name,
        description: editTeamData.description || undefined
      });

      // Refresh global context data (updates team list everywhere)
      await refreshData();

      // Refresh teams list on this page
      await refreshTeams();

      // Reload paginated teams
      await loadPaginatedTeams();

      // Close dialog
      setEditTeamDialogOpen(false);
      setSelectedTeam(null);
    } catch (error: any) {
      console.error('Failed to update team:', error);
      setEditTeamError(error?.message || 'Failed to update team');
    } finally {
      setIsEditingTeam(false);
    }
  };

  const handleConfirmDeleteTeam = async () => {
    if (!selectedTeam) {
      setDeleteTeamError('No team selected');
      return;
    }

    setIsDeletingTeam(true);
    setDeleteTeamError(null);

    try {
      await teamService.deleteTeam(selectedTeam._id);

      // Refresh global context data (updates subscriptions usage and team list everywhere)
      await refreshData();

      // Refresh teams list on this page
      await refreshTeams();

      // Reload paginated teams
      await loadPaginatedTeams();

      // Close dialog
      setDeleteTeamDialogOpen(false);
      setSelectedTeam(null);
    } catch (error: any) {
      console.error('Failed to delete team:', error);
      setDeleteTeamError(error?.message || 'Failed to delete team');
    } finally {
      setIsDeletingTeam(false);
    }
  };
  
  // Member handlers
  const handleBulkInviteComplete = () => {
    // Member list will refresh via cache invalidation performed by the service
  };

  // Leave organization handler
  const handleLeaveOrganization = async () => {
    console.log('handleLeaveOrganization called', { orgId });
    if (!orgId) {
      console.log('No orgId, returning early');
      return;
    }

    setIsLeavingOrg(true);
    try {
      console.log('Calling organizationApi.leave with orgId:', orgId);
      await organizationApi.leave(orgId);
      console.log('Leave organization successful');
      // Refresh the selector's org/team caches so the left org (and its teams)
      // drop out of the list.
      await refreshMemberships();
      setNotification({
        message: 'Successfully left organization',
        severity: 'success'
      });
      setLeaveOrgDialogOpen(false);
      // Navigate to dashboard after leaving
      navigate('/dashboard');
    } catch (error: any) {
      console.error('Error leaving organization:', error);
      console.log('Error structure:', {
        hasError: !!error,
        hasErrorProperty: !!error?.error,
        errorCode: error?.error?.code,
        errorMessage: error?.error?.message,
        hasDetails: !!error?.error?.details,
        validationErrors: error?.error?.details?.validation_errors
      });
      setLeaveOrgDialogOpen(false);

      // Check if this is a last admin error
      const isLastAdmin = isLastAdminError(error);
      console.log('Is last admin error?', isLastAdmin);

      if (isLastAdmin) {
        setNotification({
          message: 'Cannot leave organization: You are the last administrator. Please add another admin before leaving.',
          severity: 'error'
        });
      } else {
        setNotification({
          message: error?.error?.message || error?.message || 'Failed to leave organization',
          severity: 'error'
        });
      }
    } finally {
      setIsLeavingOrg(false);
    }
  };

  // Delete organization handler
  const handleDeleteOrganization = async () => {
    setIsDeletingOrg(true);
    setDeleteOrgError(null);

    const result = await deleteOrganization();

    setIsDeletingOrg(false);

    if (result.success) {
      setDeleteOrgDialogOpen(false);
      navigate('/dashboard');
    } else {
      // Display error message
      setDeleteOrgError(result.error?.message || 'Failed to delete organization');
    }
  };
  
  // Render loading state - Show loading only if we don't have organization data at all
  if (!orgId || (isLoadingOrg && !organization)) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Skeleton variant="rectangular" height={150} sx={{ mb: 3 }} />
        <Box sx={{ mb: 3 }}>
          <Skeleton variant="rectangular" height={48} />
        </Box>
        <Skeleton variant="rectangular" height={400} />
      </Container>
    );
  }
  
  // Render critical error state - only if organization completely failed to load
  if (orgError && !organization) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          Error loading organization: {orgError.message}
        </Alert>
        <Button variant="contained" onClick={() => navigate('/dashboard')}>
          Return to Dashboard
        </Button>
      </Container>
    );
  }
  
  // Organization not found - only if we've finished loading and have no organization
  if (!isLoadingOrg && !organization) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Alert severity="warning" sx={{ mb: 3 }}>
          Organization not found
        </Alert>
        <Button variant="contained" onClick={() => navigate('/dashboard')}>
          Return to Dashboard
        </Button>
      </Container>
    );
  }

  // If we have organization data, render the page even if other sections failed
  if (!organization) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 8 }}>
      {/* Organization Header */}
      <OrganizationHeader organization={organization} onUpdate={updateOrganization} />
      
      {/* Tabs Navigation */}
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Overview" id="org-tab-0" />
          <Tab label="Members" id="org-tab-1" />
          <Tab label="Roles" id="org-tab-2" />
          <Tab label="Teams" id="org-tab-3" />
          {canViewInvitations && <Tab label="Invitations" id="org-tab-4" />}
          <Tab label="Subscriptions" id={canViewInvitations ? "org-tab-5" : "org-tab-4"} />
          <Tab label="Settings" id={canViewInvitations ? "org-tab-6" : "org-tab-5"} />
        </Tabs>
      </Paper>
      
      {/* Overview Tab */}
      <TabPanel value={tabValue} index={0}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Organization Overview
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Summary and quick actions for your organization.
          </Typography>
        </Box>

        {/* Subscription Expiration Warnings */}
        <SubscriptionExpirationWarnings
          subscriptions={orgSubscriptions}
          onManageSubscription={() => {
            // Navigate to subscriptions tab
            setTabValue(canViewInvitations ? 5 : 4);
          }}
        />

        {/* Show errors for teams or members data */}
        {errors.teams && <SectionError error={errors.teams} section="teams data" onRetry={refreshTeams} />}
        {errors.members && <SectionError error={errors.members} section="members data" onRetry={refreshMembers} />}
        
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          {/* Organization Summary */}
          <Card
            sx={{
              borderRadius: 2,
              borderColor: 'divider',
              boxShadow: 'none',
              '&:hover': { transform: 'none', boxShadow: 'none' }
            }}
          >
            <CardHeader
              avatar={<InfoIcon color="info" />}
              title="Summary"
              subheader="Organization statistics"
            />
            <Divider />
            <CardContent sx={{ pt: 2 }}>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body1">Teams</Typography>
                  {loading.teams ? (
                    <CircularProgress size={16} />
                  ) : errors.teams ? (
                    <Typography variant="body1" color="text.secondary">
                      {(organization as any)?.teamCount ?? '-'}
                    </Typography>
                  ) : (
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {(organization as any)?.teamCount ?? teams.length}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body1">Members</Typography>
                  {loading.members ? (
                    <CircularProgress size={16} />
                  ) : errors.members ? (
                    <Typography variant="body1" color="text.secondary">
                      {(organization as any)?.memberCount ?? '-'}
                    </Typography>
                  ) : (
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {(organization as any)?.memberCount ?? overviewMembers.length}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </CardContent>
          </Card>
          
          {/* Quick Actions */}
          <Card
            sx={{
              borderRadius: 2,
              borderColor: 'divider',
              boxShadow: 'none',
              '&:hover': { transform: 'none', boxShadow: 'none' }
            }}
          >
            <CardHeader
              avatar={<StarIcon color="warning" />}
              title="Quick Actions"
              subheader="Common tasks you might want to do now"
            />
            <Divider />
            <CardContent sx={{ pt: 2 }}>
              <Stack spacing={1.5}>
                {canCreateTeam && (
                  <Button
                    variant="outlined"
                    startIcon={<GroupIcon />}
                    onClick={() => setCreateTeamDialogOpen(true)}
                    fullWidth
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    Create Team
                  </Button>
                )}
                {canInviteMembers && (
                  <Button
                    variant="outlined"
                    startIcon={<PersonAddIcon />}
                    onClick={() => setInviteMemberDialogOpen(true)}
                    fullWidth
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    Invite Member
                  </Button>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Box>
      </TabPanel>

      {/* Members Tab */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Organization Members
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage organization team memberships and roles
          </Typography>
        </Box>
        
        {/* Show error for members data */}
        {errors.members && <SectionError error={errors.members} section="members" onRetry={refreshMembers} />}
        
        {/* Show error for roles data */}
        {rolesError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Warning: Failed to load roles - {rolesError}. Using default roles for member invitations.
          </Alert>
        )}
        
        {isLoadingRoles ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <MembersList
            organizationId={orgId || ''} // Use orgId
            roles={roles}
            onInvite={canInviteMembers ? () => setInviteMemberDialogOpen(true) : undefined}
          />
        )}
      </TabPanel>

      {/* Roles Tab */}
      <TabPanel value={tabValue} index={2}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Organization Roles
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage custom roles and permissions for your organization members.
          </Typography>
        </Box>
        
        <RoleManager organizationId={orgId || ''} level="organization" />
      </TabPanel>

      {/* Teams Tab */}
      <TabPanel value={tabValue} index={3}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Organization Teams
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage teams within your organization.
          </Typography>
        </Box>
        
        {/* Show error for teams data */}
        {errors.teams && <SectionError error={errors.teams} section="teams" onRetry={refreshTeams} />}
        
        <Box>
          {canCreateTeam && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button
                variant="contained"
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => setCreateTeamDialogOpen(true)}
              >
                Create Team
              </Button>
            </Box>
          )}
          
          {errors.teams ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                Teams data could not be loaded. Please try refreshing the page or contact support if the problem persists.
              </Typography>
            </Paper>
          ) : (
            <DataGrid
              rows={paginatedTeams || []}
              columns={teamColumns}
              loading={teamsLoading}
              getRowId={(row: Team) => row._id || row.id}
              autoHeight
              disableRowSelectionOnClick
              sx={{
                '& .MuiDataGrid-columnHeaders': {
                  overflow: 'visible',
                },
                '& .MuiDataGrid-cell': {
                  overflow: 'visible',
                },
              }}
              density="standard"
              // Server-side pagination
              paginationMode="server"
              rowCount={teamsRowCount}
              paginationModel={teamsPaginationModel}
              onPaginationModelChange={setTeamsPaginationModel}
              pageSizeOptions={[5, 10, 25, 50, 100]}
              localeText={{
                noRowsLabel: 'No teams created yet',
                noResultsOverlayLabel: 'No teams found',
                MuiTablePagination: {
                  labelRowsPerPage: 'Teams per page',
                },
              }}
            />
          )}
        </Box>
      </TabPanel>

      {/* Invitations Tab - only render if user has permission */}
      {canViewInvitations && (
        <TabPanel value={tabValue} index={4}>
          <OrganizationInvitations organizationId={orgId || ''} userRole={userRole} />
        </TabPanel>
      )}

      {/* Subscriptions Tab */}
      <TabPanel value={tabValue} index={canViewInvitations ? 5 : 4}>
        {/* Show error for subscription data */}
        {errors.subscription && (
          <SectionError 
            error={errors.subscription} 
            section="subscription data" 
            onRetry={refreshSubscription} 
          />
        )}
        
        {errors.subscription ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              Subscription Management
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Subscription data could not be loaded. Please try refreshing or contact support if the problem persists.
            </Typography>
          </Paper>
        ) : (
          <SubscriptionManagement organizationId={orgId || ''} />
        )}
      </TabPanel>
      
      {/* Settings Tab */}
      <TabPanel value={tabValue} index={canViewInvitations ? 6 : 5}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Organization Settings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your organization's settings and configuration.
          </Typography>
        </Box>

        {/* Organization Details Section */}
        {organization && (
          <OrganizationSettings
            organization={organization as any}
            onUpdateOrganization={updateOrganization}
          />
        )}

        {/* Organization Actions Section */}
        <Paper sx={{ p: 3, mb: 3, mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Organization Actions
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Manage your membership and organization operations.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Leave Organization */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  Leave this organization
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Remove yourself from this organization. You will lose access to all organization resources.
                </Typography>
              </Box>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => {
                  console.log('Leave Organization button clicked');
                  setLeaveOrgDialogOpen(true);
                }}
              >
                Leave Organization
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Danger Zone - Only show if user can delete */}
        {canDeleteOrganization && (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ color: 'error.main' }}>
              Danger Zone
            </Typography>
            <Paper
              sx={{
                p: 3,
                border: '1px solid',
                borderColor: 'error.light',
                bgcolor: 'error.lighter'
              }}
            >
              {/* Delete Organization */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    Delete this organization
                  </Typography>
                  <Typography variant="body2">
                    Once you delete an organization, there is no going back. Please be certain.
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => setDeleteOrgDialogOpen(true)}
                >
                  Delete
                </Button>
              </Box>
            </Paper>
          </Box>
        )}
      </TabPanel>
      
      {/* Dialogs */}
      <CreateTeamDialog
        open={createTeamDialogOpen}
        onClose={() => setCreateTeamDialogOpen(false)}
        onSubmit={handleCreateTeam}
        organizationId={orgId || ''} // Use orgId
      />

      {/* Edit Team Dialog */}
      <Dialog open={editTeamDialogOpen} onClose={handleCloseEditTeam} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit Team
          <IconButton
            aria-label="close"
            onClick={handleCloseEditTeam}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {editTeamError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {editTeamError}
            </Alert>
          )}

          <TextField
            margin="normal"
            required
            fullWidth
            id="name"
            label="Team Name"
            name="name"
            value={editTeamData.name}
            onChange={handleEditTeamChange('name')}
            error={!!editTeamError && !editTeamData.name}
            disabled={isEditingTeam}
            autoFocus
          />

          <TextField
            margin="normal"
            fullWidth
            id="description"
            label="Description"
            name="description"
            value={editTeamData.description}
            onChange={handleEditTeamChange('description')}
            multiline
            rows={3}
            disabled={isEditingTeam}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseEditTeam} disabled={isEditingTeam}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitEditTeam}
            color="primary"
            disabled={isEditingTeam || !editTeamData.name || !editTeamData.name.trim()}
            startIcon={<SaveIcon />}
            variant="contained"
          >
            {isEditingTeam ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Team Confirm Dialog */}
      <ConfirmDialog
        open={deleteTeamDialogOpen}
        title="Delete Team"
        content={
          <>
            {`Are you sure you want to delete the team "${selectedTeam?.name}"? This action cannot be undone and will remove all team members and associated data.`}
            {deleteTeamError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {deleteTeamError}
              </Alert>
            )}
          </>
        }
        confirmText={isDeletingTeam ? "Deleting..." : "Delete"}
        cancelText="Cancel"
        onConfirm={handleConfirmDeleteTeam}
        onCancel={() => {
          setDeleteTeamDialogOpen(false);
          setDeleteTeamError(null);
          setSelectedTeam(null);
        }}
        severity="error"
        isLoading={isDeletingTeam}
      />

      <BulkInviteDialog
        open={inviteMemberDialogOpen}
        onClose={() => setInviteMemberDialogOpen(false)}
        onComplete={handleBulkInviteComplete}
        organizationId={orgId || ''}
      />
      
      <ConfirmDialog
        open={deleteOrgDialogOpen}
        title="Delete Organization"
        content={
          <>
            {`Are you sure you want to delete ${organization?.name || 'this organization'}? This action cannot be undone and will remove all data associated with this organization, including teams, members, and settings.`}
            {deleteOrgError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {deleteOrgError}
              </Alert>
            )}
          </>
        }
        confirmText={isDeletingOrg ? "Deleting..." : "Delete Organization"}
        cancelText="Cancel"
        onConfirm={handleDeleteOrganization}
        onCancel={() => {
          setDeleteOrgDialogOpen(false);
          setDeleteOrgError(null);
        }}
        severity="error"
        isLoading={isDeletingOrg}
      />

      {/* Leave Organization Confirm Dialog */}
      <ConfirmDialog
        open={leaveOrgDialogOpen}
        title="Leave Organization"
        content="Are you sure you want to leave this organization? You will lose access to all organization resources, teams, and content. This action cannot be undone."
        confirmText="Leave Organization"
        cancelText="Cancel"
        onConfirm={handleLeaveOrganization}
        onCancel={() => {
          console.log('Leave Organization dialog cancelled');
          setLeaveOrgDialogOpen(false);
        }}
        severity="warning"
        isLoading={isLeavingOrg}
      />

      {/* Confirmation Dialog from useConfirmation hook */}
      {confirmationState && (
        <ConfirmDialog
          open={isOpen}
          title={confirmationState.title}
          content={confirmationState.message}
          confirmText={confirmationState.confirmText || "Confirm"}
          cancelText={confirmationState.cancelText || "Cancel"}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
          severity={confirmationState.severity}
        />
      )}

      {/* Notification Snackbar */}
      <Snackbar
        open={Boolean(notification)}
        autoHideDuration={6000}
        onClose={() => setNotification(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={() => setNotification(null)}
          severity={notification?.severity || 'info'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification?.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default OrganizationDetail;
